'use client';

import { useRef, useEffect, useState, useCallback } from 'react';

interface TabBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  categories: string[];
}

export default function TabBar({ activeTab, onTabChange, categories }: TabBarProps) {
  const tabBarRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [underlineStyle, setUnderlineStyle] = useState({
    width: 0,
    left: 0,
  });

  // underline 위치 업데이트 함수
  const updateUnderlinePosition = useCallback(() => {
    if (activeTab && scrollContainerRef.current) {
      const activeButton = scrollContainerRef.current.querySelector(`[data-category="${activeTab}"]`) as HTMLElement;

      if (activeButton) {
        setUnderlineStyle({
          width: activeButton.offsetWidth,
          left: activeButton.offsetLeft,
        });
      }
    }
  }, [activeTab]);

  // activeTab이 변경될 때 해당 탭이 보이는지 확인하고 스크롤
  useEffect(() => {
    if (activeTab && scrollContainerRef.current) {
      const activeButton = scrollContainerRef.current.querySelector(`[data-category="${activeTab}"]`) as HTMLElement;

      if (activeButton) {
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // 버튼이 컨테이너 밖으로 나갔는지 확인
        const isOutOfView = buttonRect.left < containerRect.left || buttonRect.right > containerRect.right;

        if (isOutOfView) {
          // 부드럽게 스크롤하여 버튼을 중앙에 위치시킴
          const scrollLeft = activeButton.offsetLeft - container.offsetWidth / 2 + activeButton.offsetWidth / 2;

          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }
    }

    // underline 위치 업데이트
    updateUnderlinePosition();
  }, [activeTab, updateUnderlinePosition]);

  // 컴포넌트 마운트 및 리사이즈 시 underline 위치 업데이트
  useEffect(() => {
    updateUnderlinePosition();

    const handleResize = () => {
      updateUnderlinePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab, categories, updateUnderlinePosition]);

  if (categories.length === 0) {
    return null; // 카테고리가 없으면 TabBar를 표시하지 않음
  }

  const handleTabClick = (category: string) => {
    // 상태 업데이트
    onTabChange(category);

    // 해당 섹션으로 스크롤 이동
    const element = document.getElementById(`section-${category}`);
    if (element && tabBarRef.current) {
      // 실제 DOM 요소 높이 측정
      const headerHeight = 50; // Header는 고정 높이
      const tabBarHeight = tabBarRef.current.offsetHeight;
      const offset = headerHeight + tabBarHeight + 5; // 약간의 여유 공간

      const elementPosition = element.offsetTop - offset;

      window.scrollTo({
        top: Math.max(0, elementPosition), // 음수 방지
        behavior: 'smooth',
      });
    }
  };

  return (
    <div ref={tabBarRef} className="sticky top-[50px] z-40 bg-white">
      <div className="">
        <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide px-5 relative">
          <div className="flex gap-6 w-max">
            {categories.map(category => (
              <button
                key={category}
                data-category={category}
                onClick={() => handleTabClick(category)}
                className={`flex-shrink-0 py-2.5 px-0 text-base text-md font-semibold transition-colors whitespace-nowrap ${
                  activeTab === category ? 'text-gray-700' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* 애니메이션 underline */}
          <div
            className="absolute bottom-0.5 h-0.5 bg-gray-700 transition-all duration-150 ease-in-out"
            style={{
              width: `${underlineStyle.width}px`,
              left: `${underlineStyle.left}px`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
